import {
  Addon,
  AddonDiscount,
  AddonPrices,
  ProductOverview,
  QuotaDiscount,
  QuotaPackage
} from '@ui/shared/model';
import { ActionState, ActionStateCreator } from '@ui/legacy-lib';
import {
  CREATE_PRODUCT,
  CREATE_SUBSCRIPTION,
  CREATE_SUBSCRIPTION_FAIL,
  CREATE_SUBSCRIPTION_SUCCESS,
  LOAD_AVAILABLE_PRODUCTS,
  LOAD_AVAILABLE_PRODUCTS_FAIL,
  LOAD_AVAILABLE_PRODUCTS_SUCCESS,
  LOAD_CUSTOMER_PRODUCT,
  LOAD_CUSTOMER_PRODUCT_FAIL,
  LOAD_CUSTOMER_PRODUCT_SUCCESS,
  LOAD_DIGITAL_CONTRACT_QUOTA_DISCOUNT_SUCCESS,
  LOAD_DIGITAL_CONTRACT_QUOTA_PACKAGES_SUCCESS,
  LOAD_OBJECT_CONTINGENT_QUOTA_DISCOUNT_SUCCESS,
  LOAD_OBJECT_CONTINGENT_QUOTA_PACKAGES_SUCCESS,
  RECALCULATE_ADDONS_PRICE,
  RECALCULATE_ADDONS_PRICE_FAIL,
  RECALCULATE_ADDONS_PRICE_SUCCESS,
  RECALCULATE_ADDON_PRICE,
  RECALCULATE_ADDON_PRICE_FAIL,
  RECALCULATE_ADDON_PRICE_SUCCESS,
  REMOVE_CUSTOMER_MODULES_SUCCESS,
  RESET_PRODUCT,
  SELECT_DEFAULT_PRODUCT,
  SELECT_PRODUCT,
  SubscriptionAction,
  UPDATE_NEXT_PRODUCT_SUCCESS,
  UPDATE_SUBSCRIPTION,
  UPDATE_SUBSCRIPTION_FAIL,
  UPDATE_SUBSCRIPTION_SUCCESS
} from './products.actions';

export interface ProductsState {
  selectedProductId: string;
  landlordProducts: { [key: string]: ProductOverview };
  landlordProductsActionState: ActionState;
  recalculatingPricesActionState: ActionState;
  defaultProductId: string; // default product is used for new users, without CustomerProduct
  defaultProducts: ProductOverview[];
  defaultProductsActionState: ActionState;
  addonDiscounts: AddonDiscount[];
  digitalContractQuotaDiscounts: QuotaDiscount[];
  digitalContractQuotaPackages: QuotaPackage[];
  objectContingentQuotaDiscounts: QuotaDiscount[];
  objectContingentQuotaPackages: QuotaPackage[];
  subscriptionActionState: ActionState;
}

export const initialState: ProductsState = {
  landlordProducts: {},
  landlordProductsActionState: ActionStateCreator.create(),
  defaultProducts: [],
  defaultProductsActionState: ActionStateCreator.create(),
  defaultProductId: '100001',
  selectedProductId: null,
  recalculatingPricesActionState: ActionStateCreator.create(),
  addonDiscounts: [],
  digitalContractQuotaDiscounts: [],
  digitalContractQuotaPackages: [],
  objectContingentQuotaDiscounts: [],
  objectContingentQuotaPackages: [],
  subscriptionActionState: ActionStateCreator.create()
};

export function reducer(state = initialState, action: SubscriptionAction) {
  switch (action.type) {
    case REMOVE_CUSTOMER_MODULES_SUCCESS: {
      const product = state.landlordProducts[action.customerId];
      if (!product) return state;
      return {
        ...state,
        landlordProducts: {
          ...state.landlordProducts,
          [action.customerId]: {
            ...product,
            addons: (product.addons ?? []).filter(
              addon => !action.addonTypes.includes(addon.type)
            )
          }
        }
      };
    }

    case LOAD_CUSTOMER_PRODUCT: {
      return {
        ...state,
        landlordProductsActionState: ActionStateCreator.onStart()
      };
    }

    case LOAD_CUSTOMER_PRODUCT_FAIL: {
      return {
        ...state,
        landlordProductsActionState: ActionStateCreator.onError(action.error)
      };
    }

    case LOAD_CUSTOMER_PRODUCT_SUCCESS: {
      const landlordProducts = {
        ...state.landlordProducts,
        [action.customerID]: action.product
      };
      const addonDiscounts = action?.product?.addons?.map(addon => ({
        ...addon?.discount,
        addonId: addon.id
      }));
      return {
        ...state,
        landlordProducts,
        defaultProductId: action.product?.product?.id || state.defaultProductId,
        landlordProductsActionState: ActionStateCreator.onSuccess(),
        addonDiscounts
      };
    }

    case LOAD_AVAILABLE_PRODUCTS: {
      return {
        ...state,
        defaultProductsActionState: ActionStateCreator.onStart()
      };
    }

    case LOAD_AVAILABLE_PRODUCTS_SUCCESS: {
      const defaultProducts = action.products;

      return {
        ...state,
        defaultProducts,
        defaultProductsActionState: ActionStateCreator.onSuccess()
      };
    }

    case LOAD_AVAILABLE_PRODUCTS_FAIL: {
      return {
        ...state,
        defaultProductsActionState: ActionStateCreator.onError(action.error)
      };
    }

    case SELECT_DEFAULT_PRODUCT: {
      const defaultProductId = action.productId;

      return {
        ...state,
        defaultProductId
      };
    }

    case SELECT_PRODUCT: {
      const selectedProductId = action.productId;

      return {
        ...state,
        selectedProductId
      };
    }

    case LOAD_DIGITAL_CONTRACT_QUOTA_DISCOUNT_SUCCESS: {
      return {
        ...state,
        digitalContractQuotaDiscounts: action.quotaDiscount
      };
    }

    case LOAD_DIGITAL_CONTRACT_QUOTA_PACKAGES_SUCCESS: {
      return {
        ...state,
        digitalContractQuotaPackages: action.quotaPackages
      };
    }

    case LOAD_OBJECT_CONTINGENT_QUOTA_DISCOUNT_SUCCESS: {
      return {
        ...state,
        objectContingentQuotaDiscounts: action.quotaDiscount
      };
    }

    case LOAD_OBJECT_CONTINGENT_QUOTA_PACKAGES_SUCCESS: {
      return {
        ...state,
        objectContingentQuotaPackages: action.quotaPackages
      };
    }

    case RECALCULATE_ADDON_PRICE: {
      return {
        ...state,
        recalculatingPricesActionState: ActionStateCreator.onStart()
      };
    }

    case RECALCULATE_ADDON_PRICE_FAIL: {
      return {
        ...state,
        recalculatingPricesActionState: ActionStateCreator.onError(action.error)
      };
    }

    case RECALCULATE_ADDON_PRICE_SUCCESS: {
      let landlordProduct = state.landlordProducts[action.customerId];
      const isInProduct = landlordProduct.addons.some(
        a => a.id === action.addonId
      );
      const addons = isInProduct
        ? changePriceOfAddon(landlordProduct.addons, action)
        : [
            ...landlordProduct.addons,
            {
              id: action.addonId,
              price: action.prices.price,
              nextPrice: action.prices.nextPrice
            }
          ];
      landlordProduct = {
        ...landlordProduct,
        addons
      };
      return {
        ...state,
        recalculatingPricesActionState: ActionStateCreator.onSuccess(),
        landlordProducts: {
          ...state.landlordProducts,
          [action.customerId]: landlordProduct
        }
      };
    }

    case RECALCULATE_ADDONS_PRICE: {
      return {
        ...state,
        recalculatingPricesActionState: ActionStateCreator.onStart()
      };
    }

    case RECALCULATE_ADDONS_PRICE_FAIL: {
      return {
        ...state,
        recalculatingPricesActionState: ActionStateCreator.onError(action.error)
      };
    }

    case RECALCULATE_ADDONS_PRICE_SUCCESS: {
      let landlordProduct = state.landlordProducts[action.customerId];
      let defaultProducts = state.defaultProducts;
      landlordProduct = {
        ...landlordProduct,
        addons: changePriceOfMultipleAddons(
          landlordProduct.addons,
          action.addons
        )
      };

      defaultProducts = defaultProducts.map(p => {
        if (
          p.product.id ===
          state.landlordProducts[action.customerId]?.product?.id
        ) {
          return {
            ...p,
            addons: changePriceOfMultipleAddons(p.addons, action.addons)
          };
        }
        return p;
      });
      return {
        ...state,
        landlordProducts: {
          ...state.landlordProducts,
          [action.customerId]: landlordProduct
        },
        defaultProducts,
        recalculatingPricesActionState: ActionStateCreator.onSuccess()
      };
    }

    case UPDATE_SUBSCRIPTION:
    case CREATE_SUBSCRIPTION: {
      return {
        ...state,
        subscriptionActionState: ActionStateCreator.onStart()
      };
    }

    case UPDATE_SUBSCRIPTION_FAIL:
    case CREATE_SUBSCRIPTION_FAIL: {
      return {
        ...state,
        subscriptionActionState: ActionStateCreator.onError(action.error)
      };
    }

    case UPDATE_SUBSCRIPTION_SUCCESS:
    case CREATE_SUBSCRIPTION_SUCCESS: {
      return {
        ...state,
        subscriptionActionState: ActionStateCreator.onSuccess(),
        landlordProducts: {
          ...state.landlordProducts,
          [action.customerID]: action.product
        }
      };
    }

    case UPDATE_NEXT_PRODUCT_SUCCESS: {
      return {
        ...state,
        nextProductId: action.productId
      };
    }

    case RESET_PRODUCT: {
      return {
        ...initialState,
        addonDiscounts: []
      };
    }

    case CREATE_PRODUCT: {
      return {
        ...state,
        landlordProducts: {
          ...state.landlordProducts,
          new: state.defaultProducts.find(
            p => p.product.id === state.defaultProductId
          )
        },
        landlordProductsActionState: ActionStateCreator.onSuccess()
      };
    }

    default: {
      return state;
    }
  }
}

function changePriceOfMultipleAddons(
  addons: Addon[],
  newAddon: Addon[]
): Addon[] {
  return (addons || []).map(a => {
    const foundIndex = newAddon.findIndex(na => na.id === a.id);
    if (foundIndex > -1) {
      return {
        ...a,
        price: newAddon[foundIndex].price,
        nextPrice: newAddon[foundIndex].nextPrice
      };
    }
    return a;
  });
}

function changePriceOfAddon(
  addons: Addon[],
  action: { addonId: string; prices: AddonPrices }
): Addon[] {
  return (addons || []).map(ca => {
    if (ca.id === action.addonId) {
      return {
        ...ca,
        price: action.prices.price,
        nextPrice: action.prices.nextPrice
      };
    }
    return ca;
  });
}

export const getLandlordProducts = (state: ProductsState) =>
  state.landlordProducts;
export const getLandlordProductsActionState = (state: ProductsState) =>
  state.landlordProductsActionState;
export const getDefaultProducts = (state: ProductsState) =>
  state.defaultProducts;
export const getDefaultProductsActionState = (state: ProductsState) =>
  state.defaultProductsActionState;
export const getSelectedProductId = (state: ProductsState) =>
  state.selectedProductId;
export const getDefaultProductId = (state: ProductsState) =>
  state.defaultProductId;
export const getRecalculatingPricesActionState = (state: ProductsState) =>
  state.recalculatingPricesActionState;
export const getAddonDiscounts = (state: ProductsState) => state.addonDiscounts;
export const getDigitalContractQuotaDiscounts = (state: ProductsState) =>
  state.digitalContractQuotaDiscounts;
export const getDigitalContractQuotaPackages = (state: ProductsState) =>
  state.digitalContractQuotaPackages;
export const getObjectContingentQuotaDiscounts = (state: ProductsState) =>
  state.objectContingentQuotaDiscounts;
export const getObjectContingentQuotaPackages = (state: ProductsState) =>
  state.objectContingentQuotaPackages;
export const getMapOfValidAddonDiscounts = (state: ProductsState) => {
  return;
  state.addonDiscounts.filter(
    discount => new Date(discount.endDate).getTime() > new Date().getTime()
  );
};
export const getSubscriptionActionState = (state: ProductsState) =>
  state.subscriptionActionState;
